import type {
  CollectedStudyResults,
  EvidenceStatus,
  Project,
  ReportAgentPackage,
  SimulationEvidence,
} from '@enersu/core-orchestrator';
import type { TDocumentDefinitions } from 'pdfmake/interfaces';

import {
  buildHebrewReportShell,
  type GenericAnalysisMetric,
  type GenericAnalysisOutput,
  type GenericAnalysisStatus,
  type HebrewReportMessages,
} from './report-shell';

/**
 * Municipal report package (`report-agent-package/v1`). The contract types are
 * owned by @enersu/core-orchestrator and re-exported here for convenience.
 * Outputs remain screening/evidence only and never assert compliance.
 */
export type {
  EvidenceFinding,
  EvidenceMetric,
  EvidenceStatus,
  ReportAgentPackage,
  SimulationEvidence,
} from '@enersu/core-orchestrator';

export interface BuildReportAgentPackageInput {
  project: { name: string; municipalityId: string; timezone: string };
  municipalityId: string;
  language: 'he' | 'en';
  generatedAt: Date;
  studies: readonly SimulationEvidence[];
  disclaimer: string;
}

const EVIDENCE_STATUS_TO_ANALYSIS: Record<EvidenceStatus, GenericAnalysisStatus> = {
  'screening-ready': 'complete',
  'needs-review': 'needs-review',
  blocked: 'error',
};

/** Assembles a serializable `report-agent-package/v1` from simulation evidence. */
export function buildReportAgentPackage(input: BuildReportAgentPackageInput): ReportAgentPackage {
  return {
    schemaVersion: 'report-agent-package/v1',
    municipalityId: input.municipalityId,
    language: input.language,
    generatedAt: input.generatedAt.toISOString(),
    project: {
      name: input.project.name,
      municipalityId: input.project.municipalityId,
      timezone: input.project.timezone,
    },
    studies: input.studies,
    disclaimer: input.disclaimer,
  };
}

/** Maps a single simulation evidence entry to a generic report analysis. */
export function simulationEvidenceToAnalysis(evidence: SimulationEvidence): GenericAnalysisOutput {
  const metrics: GenericAnalysisMetric[] = evidence.metrics.map((metric) => ({
    label: metric.label,
    value: metric.value,
    ...(metric.unit !== undefined ? { unit: metric.unit } : {}),
  }));

  const notes: string[] = evidence.findings.map(
    (finding) => `[${finding.severity}] ${finding.code}: ${finding.message}`,
  );

  return {
    id: evidence.studyId,
    title: evidence.studyId,
    status: EVIDENCE_STATUS_TO_ANALYSIS[evidence.status],
    ...(metrics.length > 0 ? { metrics } : {}),
    ...(notes.length > 0 ? { notes } : {}),
  };
}

/**
 * Maps a results-collector output into the generic report analyses the Hebrew
 * report shell consumes (pipeline Stage 6 → report shell). Preserves the
 * collector's canonical study order, so screening evidence can be surfaced
 * through `buildHebrewReportShell` alongside other pipeline analyses.
 */
export function collectedStudiesToReportAnalyses(
  collected: CollectedStudyResults,
): GenericAnalysisOutput[] {
  return collected.studies.map(simulationEvidenceToAnalysis);
}

/**
 * Renders a `report-agent-package/v1` into a Hebrew RTL pdfmake document by
 * reusing the shared report shell. The package disclaimer is preserved over
 * the bundled message disclaimer so screening-only intent is never lost.
 */
export function reportAgentPackageToDocument(
  pkg: ReportAgentPackage,
  messages: HebrewReportMessages,
): TDocumentDefinitions {
  const project: Project = {
    id: pkg.project.name,
    name: pkg.project.name,
    timezone: pkg.project.timezone,
    municipalityId: pkg.project.municipalityId,
    site: {
      id: pkg.project.name,
      terrainPaths: [],
      boundaryPaths: [],
    },
    buildings: [],
  };

  const analyses = pkg.studies.map(simulationEvidenceToAnalysis);

  const shellMessages: HebrewReportMessages = {
    ...messages,
    report: {
      ...messages.report,
      disclaimer: pkg.disclaimer,
    },
  };

  return buildHebrewReportShell({
    project,
    generatedAt: new Date(pkg.generatedAt),
    analyses,
    messages: shellMessages,
  });
}
